import { Typography, Button, styled, Link } from '@mui/material';
import { useLocation, Location, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState, useContext } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { WeavyTextLogo } from '@/UI/Icons/WeavyTextLogo';
import { Copyrights } from '@/components/Common/Copyrights';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import MicrosoftLogo from '@/UI/Icons/MicrosoftLogo';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { AuthContext } from '@/contexts/AuthContext';
import { FlexColCenHorVer, Flex, FlexCol } from '@/UI/styles';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { DescopeSignInButtons } from '@/components/Common/Authentication/DescopeSignInButtons';
import { AUTH_PROVIDERS, DESCOPE_FLOW_IDS } from '@/consts/auth.consts';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { FF_TERMS_OF_SERVICE } from '@/consts/featureFlags';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { IDP_INITIATED_REDIRECT_PATH } from './IdpInitiatedSignInPage';
import type { SignInOptions } from '@/contexts/AuthContext';

const StyledSignInButton = styled(Button)(() => ({
  alignItems: 'center',
  borderRadius: '8px',
  width: '234px',
  backgroundColor: color.Black92,
  color: color.White100,
  display: 'flex',
  height: '36px',
  gap: '8px',
  '&:hover': {
    backgroundColor: color.Black84,
  },
}));

const StyledSecondarySignInButton = styled(Button)(() => ({
  alignItems: 'center',
  borderRadius: '8px',
  width: '234px',
  backgroundColor: color.White100,
  color: color.Black84,
  display: 'flex',
  height: '36px',
  gap: '8px',
  border: `1px solid ${color.Black16}`,
  '&:hover': {
    backgroundColor: color.Black16,
  },
}));

const useWrappedLocation = () => {
  const location = useLocation();
  return location as Location<{ from?: string }>;
};

const FirebaseSignInButtons = ({
  signIn,
  from,
  promotion,
  initiateRedirectSignIn,
}: {
  signIn: (options: SignInOptions) => Promise<void>;
  from: string;
  promotion: string | null;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
}) => {
  const { t } = useTranslation();

  const googleSignIn = useCallback(
    () =>
      void signIn({
        route: from,
        providerType: 'google',
        promotion,
      }),
    [signIn, from, promotion],
  );

  const microsoftSignIn = useCallback(
    () =>
      void signIn({
        route: from,
        providerType: 'microsoft',
        promotion,
      }),
    [signIn, from, promotion],
  );

  const figmaSignIn = useCallback(() => {
    void initiateRedirectSignIn('figma', '/signin');
  }, [initiateRedirectSignIn]);

  return (
    <FlexCol sx={{ gap: 1, mb: 6 }}>
      <>
        <StyledSignInButton variant="contained" onClick={figmaSignIn}>
          <Flex sx={{ alignItems: 'center', gap: 1 }}>
            <FigmaLogo />
            <Typography variant="body-std-rg" color={color.White100}>
              {t(I18N_KEYS.SIGN_IN_PAGE.FIGMA_SIGN_IN_BUTTON)}
            </Typography>
          </Flex>
        </StyledSignInButton>
        <Typography variant="body-std-rg" color={color.Black64_T} sx={{ textAlign: 'center', my: 0.5 }}>
          {t(I18N_KEYS.SIGN_IN_PAGE.OR)}
        </Typography>
      </>
      <StyledSecondarySignInButton variant="outlined" onClick={googleSignIn}>
        <Flex sx={{ width: '170px', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
          <GoogleLogo width={20} height={20} title="Google logo" />
          <Typography variant="body-std-rg" color={color.Black92}>
            {t(I18N_KEYS.SIGN_IN_PAGE.GOOGLE_SIGN_IN_BUTTON)}
          </Typography>
        </Flex>
      </StyledSecondarySignInButton>
      <StyledSecondarySignInButton variant="outlined" onClick={microsoftSignIn}>
        <Flex sx={{ width: '170px', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
          <MicrosoftLogo width={20} height={20} title="Microsoft logo" />
          <Typography variant="body-std-rg" color={color.Black92}>
            {t(I18N_KEYS.SIGN_IN_PAGE.MICROSOFT_SIGN_IN_BUTTON)}
          </Typography>
        </Flex>
      </StyledSecondarySignInButton>
    </FlexCol>
  );
};

export function SignInPage() {
  const { t } = useTranslation();
  const { provider } = useAuthConfig();
  const isDescope = provider === AUTH_PROVIDERS.DESCOPE;
  const navigate = useNavigate();
  const location = useWrappedLocation();
  const [searchParams] = useSearchParams();
  const promotion = searchParams.get('promotion');
  const { offset } = useBanner(APP_BANNER_ID);
  const from: string = typeof location.state?.from === 'string' ? location.state.from : '/';
  const {
    currentUser,
    signIn,
    isSigningIn,
    onSignInSuccess,
    onSignInError,
    initiateRedirectSignIn,
    getStoredPromotion,
  } = useContext(AuthContext);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSigninInProgress, setIsSigninInProgress] = useState(false);
  const { value: isTermsOfServiceEnabled } = useFeatureFlag(FF_TERMS_OF_SERVICE);

  // Handle Figma OAuth redirect
  useEffect(() => {
    const authorizationCode = searchParams.get('code');
    if (authorizationCode && !currentUser && !isSigningIn) {
      setIsSigninInProgress(true);
      // For Figma OAuth, use stored promotion and redirect path (post-login redirect) from sessionStorage
      const storedPromotion = getStoredPromotion();
      const storedRedirectPath = sessionStorage.getItem(IDP_INITIATED_REDIRECT_PATH);
      sessionStorage.removeItem(IDP_INITIATED_REDIRECT_PATH);
      void signIn({
        route: storedRedirectPath ?? from,
        providerType: 'figma',
        authorizationCode,
        promotion: storedPromotion ?? promotion,
      });
    } else {
      setIsSigninInProgress(false);
    }
  }, [searchParams, currentUser, isSigningIn, signIn, from, promotion, getStoredPromotion]);

  useEffect(() => {
    if (currentUser && !isSigningIn) {
      // Get the redirect path from location state, default to '/' if not present
      const authorizationCode = searchParams.get('code');
      if (authorizationCode) {
        // navigation is handled directly in oauth flow
        return;
      }
      navigate(from);
    } else if (!currentUser) {
      setAuthChecked(true);
    }
  }, [currentUser, isSigningIn, navigate, from, searchParams]);

  const goHome = useCallback(() => (window.location.href = 'https://weavy.ai'), []);

  if (!authChecked) {
    return null;
  }
  if (isSigninInProgress) {
    return <PageLoader />;
  }

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: `calc(100vh - ${offset}px)`,
        width: '100%',
        bgcolor: color.White100,
        position: 'relative',
        backgroundImage:
          'linear-gradient(0deg, #ffffff4d 34%, #c1cdd559 71%), url(https://cdn.prod.website-files.com/681b040781d5b5e278a69989/681ccdbeb607e939f7db68fa_BG%20NET%20Hero.avif)',
        backgroundPosition: '0 0, 50%',
        backgroundSize: 'auto, cover',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <FlexCol sx={{ flex: 1 }}>
        <Flex sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
          <WeavyTextLogo style={{ color: color.White100, cursor: 'pointer' }} onClick={goHome} />
        </Flex>
        <Flex sx={{ display: { xs: 'flex', sm: 'none' }, position: 'absolute', top: 12, left: 12 }}>
          <WeavyLogoFilled
            title="Weavy Logo"
            width={36}
            height={36}
            style={{
              color: color.Black92,
              cursor: 'pointer',
            }}
            onClick={goHome}
          />
        </Flex>

        {/* Main Content */}
        <Flex
          data-testid="signin-page-content"
          sx={{
            gap: { xs: 0, sm: 6 },
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: { xs: '100%', sm: '362px' },
            height: { xs: '100%', sm: 'auto' },
            margin: { xs: '0', sm: 'auto' },
            borderRadius: { xs: 0, sm: 2 },
            bgcolor: color.White100,
            border: { xs: 'none', sm: `1px solid ${color.Black04}` },
          }}
        >
          <img
            src="/assets/weavy-sign-in-back.png"
            alt="Sign in cover"
            style={{ width: '100%', height: '270px', borderRadius: '8px 8px 0px 0px' }}
          />
          <FlexColCenHorVer sx={{}}>
            <Typography
              variant="h1"
              color={color.Black92}
              sx={{ fontSize: '28px', fontWeight: 400, mb: 1.5, mt: { xs: 6, sm: 0 } }}
            >
              {t(I18N_KEYS.SIGN_IN_PAGE.TITLE)}
            </Typography>
            {!isTermsOfServiceEnabled && (
              <Typography variant="body-lg-rg" color={color.Black64_T} sx={{ mb: 4, fontSize: '14px' }}>
                {t(I18N_KEYS.SIGN_IN_PAGE.SUBTITLE)}
              </Typography>
            )}
            {isTermsOfServiceEnabled && (
              <Typography
                variant="body-sm-rg"
                color={color.Black64_T}
                sx={{ mb: 3, textAlign: 'center', maxWidth: '234px', fontSize: '10px' }}
              >
                {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_TEXT)}{' '}
                <Link
                  href={EXTERNAL_LINKS.termsOfService}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
                >
                  {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_OF_SERVICE_LINK)}
                </Link>{' '}
                and{' '}
                <Link
                  href={EXTERNAL_LINKS.privacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
                >
                  {t(I18N_KEYS.SIGN_IN_PAGE.PRIVACY_POLICY_LINK)}
                </Link>
                .
              </Typography>
            )}
            {isDescope ? (
              <Flex sx={{ width: '234px', marginBottom: '48px' }}>
                <DescopeSignInButtons
                  flowId={DESCOPE_FLOW_IDS.MAIN_SIGN_IN_SCREEN}
                  onSignInSuccess={onSignInSuccess}
                  onSignInError={onSignInError}
                  promotion={promotion}
                  from={from}
                />
              </Flex>
            ) : (
              <FirebaseSignInButtons
                signIn={signIn}
                from={from}
                promotion={promotion}
                initiateRedirectSignIn={initiateRedirectSignIn}
              />
            )}
          </FlexColCenHorVer>
        </Flex>
      </FlexCol>
      <Flex
        sx={{
          py: 3,
          px: 3,
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-end' },
        }}
      >
        <Copyrights textColor={color.Black84_T} />
      </Flex>
    </Flex>
  );
}
